import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function ConsultantEditor({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [photo, setPhoto] = useState(user.profile_photo || "");
  const [busy, setBusy] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [confirming, setConfirming] = useState(Boolean(user.deleting));
  const [confirmEmail, setConfirmEmail] = useState("");
  const dialog = useRef(null);
  const lock = useRef(false);
  const opener = useRef(document.activeElement);
  useEffect(() => {
    const element = dialog.current;
    element.showModal();
    return () => { element.close(); opener.current?.focus(); };
  }, []);
  const choosePhoto = async event => {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      toast.error("Escolha uma foto JPG, PNG ou WebP de até 10 MB"); return;
    }
    setLoadingPhoto(true);
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
      const canvas = document.createElement("canvas"); canvas.width = 256; canvas.height = 256;
      const ctx = canvas.getContext("2d"), side = Math.min(img.naturalWidth, img.naturalHeight);
      if (!ctx || !side) throw new Error("Foto inválida");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 256, 256);
      ctx.drawImage(img, (img.naturalWidth-side)/2, (img.naturalHeight-side)/2, side, side, 0, 0, 256, 256);
      setPhoto(canvas.toDataURL("image/jpeg", 0.85));
    } catch { toast.error("Não foi possível abrir esta foto"); }
    finally { URL.revokeObjectURL(url); setLoadingPhoto(false); }
  };
  const finish = async () => { onClose(); await onSaved(); };
  const save = async event => {
    event.preventDefault();
    if (lock.current || loadingPhoto || !name.trim()) return;
    lock.current = true; setBusy(true);
    try {
      await api.put(`/supervisor/users/${user.id}/profile`, { name: name.trim(), profile_photo: photo });
      toast.success("Nome e foto atualizados"); await finish();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Não foi possível salvar"); }
    finally { lock.current = false; setBusy(false); }
  };
  const remove = async event => {
    event.preventDefault();
    if (lock.current || confirmEmail.trim().toLowerCase() !== user.email.toLowerCase()) return;
    lock.current = true; setBusy(true);
    try {
      await api.post(`/supervisor/users/${user.id}/delete-permanently`, { confirm_email: confirmEmail.trim() });
      toast.success("Conta e dados excluídos do sistema"); await finish();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Não foi possível concluir a exclusão. Atualize a lista e tente novamente."); }
    finally { lock.current = false; setBusy(false); }
  };
  return <dialog ref={dialog} aria-labelledby="manage-consultant-title"
    onCancel={event => { if (busy || loadingPhoto) event.preventDefault(); else onClose(); }}
    className="w-[calc(100%-2rem)] max-w-md max-h-[90vh] overflow-y-auto rounded-xl border border-pink-500/30 bg-slate-950 p-5 text-slate-100 backdrop:bg-black/70">
    <h2 id="manage-consultant-title" className="text-lg font-semibold text-pink-300 mb-2">Gerenciar {user.name || user.email}</h2>
    <p className="text-xs text-slate-400 mb-4">{user.email}</p>
    {confirming ? <form onSubmit={remove} className="space-y-4">
      <p className="text-rose-300 font-semibold">Excluir definitivamente {user.name || user.email}?</p>
      <p className="text-sm text-slate-300">A conta, foto, contatos, mensagens, notas, leads e registros de disparos serão apagados do sistema. A conexão do WhatsApp será encerrada. Esta ação não pode ser desfeita pelo painel.</p>
      <label className="block text-sm">Digite o e-mail <strong>{user.email}</strong> para confirmar:
        <input autoFocus type="email" autoComplete="off" required value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} disabled={busy}
          className="mt-2 w-full rounded border border-rose-500/50 bg-slate-900 p-2" />
      </label>
      <div className="flex flex-wrap justify-end gap-3">
        <Button type="button" variant="outline" disabled={busy} onClick={() => { if (user.deleting) onClose(); else setConfirming(false); }}>Cancelar</Button>
        <Button type="submit" className="bg-rose-600 hover:bg-rose-700" disabled={busy || confirmEmail.trim().toLowerCase() !== user.email.toLowerCase()}>{busy ? "Excluindo..." : "Excluir conta e todos os dados"}</Button>
      </div>
    </form> : <form onSubmit={save} className="space-y-4">
      <div className="flex items-center gap-4">
        {photo ? <img src={photo} alt="Prévia da foto" className="h-20 w-20 rounded-full object-cover" /> : <div className="grid h-20 w-20 place-items-center rounded-full bg-pink-500/20 text-2xl">{name.slice(0,1).toUpperCase() || "?"}</div>}
        <div className="min-w-0">
          <label className="block text-sm">Foto de perfil
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={choosePhoto} disabled={busy || loadingPhoto} className="block w-full mt-2 text-xs" />
          </label>
          {photo && <button type="button" disabled={busy || loadingPhoto} onClick={() => setPhoto("")} className="text-xs text-pink-300 underline mt-2">Remover foto</button>}
        </div>
      </div>
      <label className="block text-sm">Nome
        <input autoFocus required maxLength={80} value={name} onChange={e => setName(e.target.value)} disabled={busy}
          className="mt-2 w-full rounded border border-slate-700 bg-slate-900 p-2" />
      </label>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" disabled={busy || loadingPhoto} onClick={onClose}>Cancelar</Button>
        <Button type="submit" disabled={busy || loadingPhoto || !name.trim()}>{busy ? "Salvando..." : loadingPhoto ? "Preparando foto..." : "Salvar alterações"}</Button>
      </div>
      <div className="border-t border-slate-800 pt-4">
        <Button type="button" variant="outline" className="text-rose-400 border-rose-500/40" disabled={busy || loadingPhoto} onClick={() => setConfirming(true)}>Excluir usuário</Button>
      </div>
    </form>}
  </dialog>;
}
export default function ManageConsultant({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  return <>
    <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>{user.deleting ? "Concluir exclusão" : "Gerenciar"}</Button>
    {open && <ConsultantEditor user={user} onSaved={onSaved} onClose={() => setOpen(false)} />}
  </>;
}
